import React, { useState, useEffect, useContext } from "react";
import {
  Box, Button, Card, CircularProgress, DialogContent, DialogTitle,
  Drawer, Typography, Alert, List, ListItem, ListItemText, Stack
} from "@mui/material";
import { useTranslation } from "react-i18next";
import RGL, { WidthProvider } from "react-grid-layout";
import "/node_modules/react-grid-layout/css/styles.css";
import "/node_modules/react-resizable/css/styles.css";
import AppContext from "../../utils/context";
import TableWidget from "./widgets/table";
import MarkdownWidget from "./widgets/markdown";
import PieChartWidget from "./widgets/piechart";
import CustomJsonForm from "../../components/customform";
import { buildSchema } from "../../utils/schema";
import SaveAltIcon from "@mui/icons-material/SaveAlt";

const ResponsiveGridLayout = WidthProvider(RGL);

export default function View() {
  const { events, database } = useContext(AppContext);
  const { t } = useTranslation();

  /* dashboard */
  const [widgets, setWidgets] = useState([]);
  const [layout, setLayout] = useState([]);
  const [previousLayout, setPreviousLayout] = useState([]);
  const [isEditMode, setEditMode] = useState(false);

  /* widget picker */
  const [widgetInstances, setWidgetInstances] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  /* formulaire */
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [editingWidgetUserId, setEditingWidgetUserId] = useState(null); // null = création
  const [schema, setSchema] = useState({});
  const [uischema, setUischema] = useState({});
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [hasTriedToSave, setHasTriedToSave] = useState(false);

  const helpDashboardMsg = t("dashboard.help.message", { defaultValue: undefined });
  const helpDashboardLink = t("dashboard.help.url", { defaultValue: undefined });

  useEffect(() => { loadDashboard(); }, []);

  /* ---------- chargements ---------- */

  const loadDashboard = async () => {
    const { data, error } = await database.rpc("get_dashboard_widgets");
    if (error) {
      events.dispatch("alert-message", { data: { message: t("commons.errors.fetching"), severity: "error" } });
      return;
    }
    const l = data.map(w => ({
      i: w.id, x: w.position.x, y: w.position.y, w: w.position.w, h: w.position.h,
      minW: w.settings.minW || null, maxW: w.settings.maxW || null,
      minH: w.settings.minH || null, maxH: w.settings.maxH || null,
    }));
    setWidgets(data);
    setLayout(l);
    setPreviousLayout(l);
  };

  const loadWidgetInstances = async () => {
    const { data, error } = await database.rpc("list_widgets");
    if (error) {
      events.dispatch("alert-message", { data: { message: t("commons.errors.fetching"), severity: "error" } });
      return;
    }
    setWidgetInstances(data);
  };

  /* ---------- ajout ---------- */

  const showWidgetPicker = () => { loadWidgetInstances(); setPickerOpen(true); };

  const configureWidget = async (widget) => {
    // création
    const sch = await buildSchema(database, widget.jsonschema, { widget_id: widget.id });
    setSchema(sch);
    setUischema(widget.jsonschema_form);
    setFormData({});
    setSelectedWidget(widget);
    setEditingWidgetUserId(null);
    setOpenForm(true);
    setPickerOpen(false);
    setHasTriedToSave(false);
    setFormErrors(null);
  };

  /* ---------- édition ---------- */

  const editWidget = async (widgetUserId) => {
    const { data, error } = await database
      .from("widgets_users")
      .select("id, metadata, widgets(id,name,jsonschema,jsonschema_form)")
      .eq("id", widgetUserId)
      .single();
    if (error) {
      events.dispatch("alert-message", { data: { message: t("commons.errors.fetching"), severity: "error" } });
      return;
    }
    const sch = await buildSchema(database, data.widgets.jsonschema, { widget_id: data.widgets.id, widget_user_id: data.id });
    setSchema(sch);
    setUischema(data.widgets.jsonschema_form);
    setFormData(data.metadata || {});
    setSelectedWidget(data.widgets);
    setEditingWidgetUserId(data.id);
    setOpenForm(true);
    setHasTriedToSave(false);
    setFormErrors(null);
  };

  /* ---------- sauvegarde ---------- */

  const saveWidget = async () => {
    setHasTriedToSave(true);
    if (formErrors && formErrors.length) {
      events.dispatch("alert-message", { data: { message: t("commons.errors.fix_errors"), severity: "error" } });
      return;
    }

    let error;
    if (editingWidgetUserId) {
      ({ error } = await database
        .from("widgets_users")
        .update({ metadata: formData })
        .eq("id", editingWidgetUserId));
    } else {
      ({ error } = await database.rpc("add_widget_dashboard", {
        widget_id: selectedWidget.id,
        position: { x: 0, y: 0, w: 4, h: 2 },
        metadata: formData,
      }));
    }

    if (error) {
      events.dispatch("alert-message", { data: { message: t("commons.errors.saving"), severity: "error" } });
      return;
    }
    setOpenForm(false);
    loadDashboard();
  };

  /* ---------- suppression ---------- */

  const removeWidget = async (id) => {
    const { error } = await database.from("widgets_users").delete().eq("id", id);
    if (error) {
      events.dispatch("alert-message", { data: { message: t("commons.errors.saving"), severity: "error" } });
      return;
    }
    loadDashboard();
  };

  /* ---------- déplacement ---------- */

  const onLayoutChange = async (l) => {
    const changed = l.some((it, i) => {
      const p = previousLayout[i];
      return !p || it.x !== p.x || it.y !== p.y || it.w !== p.w || it.h !== p.h;
    });
    if (!changed) return;
    const payload = l.map(it => ({ id: it.i, position: { x: it.x, y: it.y, w: it.w, h: it.h } }));
    const { error } = await database.rpc("save_dashboard_widgets", { qwidgets: payload });
    if (error) {
      events.dispatch("alert-message", { data: { message: t("commons.errors.saving"), severity: "error" } });
      return;
    }
    setPreviousLayout(l);
  };

  /* ---------- rendu ---------- */

  const widgetStyle = {
    flexGrow: 1, display: "flex", flexDirection: "column", padding: 10,
    position: "absolute", top: 0, left: 0, right: 0, bottom: 0, overflow: "auto",
  };

  const setFormResult = ({ data, errors }) => { setFormData(data); setFormErrors(errors); };

  return (
    <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", p: 2 }}>
      <Box sx={{ pb: 2, display: "flex" }}>
        <Typography sx={{ pt: 1 }}>{t("dashboard.title")}</Typography>
        <Box sx={{ flexGrow: 1 }} />
        {isEditMode &&
          <Button sx={{ mr: 1 }} size="small" variant="contained" onClick={showWidgetPicker}>
            {t("dashboard.add_widgets")}
          </Button>}
        <Button size="small" variant="outlined" onClick={() => setEditMode(!isEditMode)}>
          {isEditMode ? t("buttons.save") : t("buttons.customize")}
        </Button>
      </Box>

      <div className="wrapper" style={{ overflowY: "auto", position: "relative", overflowX: "hidden" }}>
        <ResponsiveGridLayout
          layout={layout} cols={12} rowHeight={200} margin={[20, 20]}
          containerPadding={[0, 0]} draggableCancel=".no-drag"
          onLayoutChange={onLayoutChange}
          isDraggable={isEditMode} isResizable={isEditMode}
        >
          {widgets.map(w => (
            <div key={w.id}>
              <Card style={widgetStyle}>
                <Box sx={{ mb: 1, display: "flex" }}>
                  <Typography sx={{ pt: 1 }}>{w.name}</Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  {isEditMode &&
                    <>
                      <Button
                        className="no-drag" size="small" variant="outlined"
                        onClick={() => editWidget(w.id)}
                      >
                        {t("buttons.edit")}
                      </Button>
                      <Button
                        className="no-drag" size="small" variant="outlined" color="error"
                        onClick={() => removeWidget(w.id)}
                        sx={{ ml: 1 }}
                      >
                        {t("buttons.delete")}
                      </Button>
                    </>}
                </Box>
                <Box sx={{ flexGrow: 1, position: "relative" }}>
                  {w.widget_type === "table"    && <TableWidget   {...w} />}
                  {w.widget_type === "markdown" && <MarkdownWidget {...w} />}
                  {w.widget_type === "piechart" && <PieChartWidget {...w} />}
                </Box>
              </Card>
            </div>
          ))}
        </ResponsiveGridLayout>
        {widgets.length === 0 &&
          <Box sx={{ color: "white", display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress />
          </Box>}
      </div>

      {/* Drawer sélection */}
      <Drawer anchor="right" open={pickerOpen} sx={{ overflow: "none" }}
              onClose={(_, r) => r === "backdropClick" || setPickerOpen(false)}>
        <DialogTitle sx={{ display: "flex" }}>
          <Typography>{t("dashboard.add_widgets")}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setPickerOpen(false)}>{t("buttons.close")}</Button>
        </DialogTitle>
        <DialogContent sx={{ width: { xs: "90vw", sm: "80vw", md: "70vw", lg: "60vw", xl: "50vw" } }}>
          {helpDashboardMsg &&
            <Alert severity="info" onClose={() => { }}
                   action={helpDashboardLink &&
                     <Button color="inherit" size="small" onClick={() => window.open(helpDashboardLink, "_blank")}>
                       {t("buttons.help")}
                     </Button>}>
              {helpDashboardMsg}
            </Alert>}
          <List>
            {widgetInstances.map(w =>
              <ListItem key={w.id}
                        secondaryAction={<Button onClick={() => configureWidget(w)}>{t("buttons.select")}</Button>}>
                <ListItemText primary={w.name} secondary={w.description} />
              </ListItem>)}
          </List>
        </DialogContent>
      </Drawer>

      {/* Drawer formulaire */}
      <Drawer anchor="right" open={openForm}
              onClose={(_, r) => r === "backdropClick" || setOpenForm(false)}>
        <DialogTitle sx={{ display: "flex" }}>
          <Typography>{selectedWidget?.name}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={1}>
            <Button color="success" variant="outlined" startIcon={<SaveAltIcon />} onClick={saveWidget}>
              {t("buttons.save")}
            </Button>
            <Button onClick={() => setOpenForm(false)}>{t("buttons.cancel")}</Button>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ width: { xs: "90vw", sm: "80vw", md: "70vw", lg: "60vw", xl: "50vw" } }}>
          <Box sx={{ pt: 2 }} className="jsonforms">
            <CustomJsonForm
              schema={schema} uischema={uischema} data={formData}
              onChange={({ data, errors }) => setFormResult({ data, errors })}
              contextVars={{ widget_id: selectedWidget?.id, widget_user_id: editingWidgetUserId }}
              validationMode={hasTriedToSave ? "ValidateAndShow" : "ValidateAndHide"}
              config={{ showUnfocusedDescription: true }}
            />
          </Box>
        </DialogContent>
      </Drawer>
    </Box>
  );
}
